/**
 * device_info.ts — 기기 판정·표시용 순수 함수 (firebase 의존 없음).
 *
 * 설계: docs/design/12_통합관리_계정기기설계.md §4-2 기기 · §7 세션 정책 · §10-4·5 한계.
 * 공용 약속 `domain/session.ts` 는 바꾸지 않기로 해서(2단계 4묶음 공유) 기기 종류 판정·UA 파싱은 여기에 둔다.
 *
 * UA 파싱은 표시용이다. iPadOS 는 "macOS" 로 보이고 브라우저 UA 축소 정책 때문에 버전은 적지 않는다(§10-4).
 */
import {
  INACTIVE_EXPIRE_DAYS,
  type AccessLogType,
  type DeviceDoc,
  type DeviceKind,
} from "./session";

export interface DeviceEnv {
  userAgent: string;
  /** navigator.maxTouchPoints — iPadOS(데스크톱 UA) 판정용 */
  maxTouchPoints?: number;
}

/** 04 세션 정책 `isMobile` 과 같은 식 + iPadOS(맥 UA + 터치) 보정 */
export function detectDeviceKind(env: DeviceEnv): DeviceKind {
  const ua = env.userAgent || "";
  if (/Android|iPhone|iPad|iPod/i.test(ua)) return "mo";
  if (/Macintosh/.test(ua) && (env.maxTouchPoints ?? 0) > 1) return "mo";
  return "pc";
}

/** 브라우저 이름 — 순서 중요(Edge·삼성·웨일 UA 에 Chrome 이 함께 들어 있음) */
export function parseBrowser(ua: string): string {
  if (/Edg(e|A|iOS)?\//.test(ua)) return "Edge";
  if (/Whale\//.test(ua)) return "Whale";
  if (/SamsungBrowser\//.test(ua)) return "삼성 인터넷";
  if (/KAKAOTALK/i.test(ua)) return "카카오톡";
  if (/NAVER\(inapp/i.test(ua)) return "네이버 앱";
  if (/OPR\/|Opera/.test(ua)) return "Opera";
  if (/Firefox\/|FxiOS\//.test(ua)) return "Firefox";
  if (/CriOS\/|Chrome\//.test(ua)) return "Chrome";
  if (/Safari\//.test(ua)) return "Safari";
  return "브라우저";
}

/** OS 이름 — Windows 10·11 은 UA 로 구분되지 않아 "Windows" 로만 적는다 */
export function parseOs(env: DeviceEnv): string {
  const ua = env.userAgent || "";
  if (/iPhone|iPod/.test(ua)) return "iOS";
  if (/iPad/.test(ua)) return "iPadOS";
  if (/Android/.test(ua)) return "Android";
  if (/Windows/.test(ua)) return "Windows";
  if (/CrOS/.test(ua)) return "ChromeOS";
  if (/Macintosh|Mac OS X/.test(ua)) return (env.maxTouchPoints ?? 0) > 1 ? "iPadOS" : "macOS";
  if (/Linux/.test(ua)) return "Linux";
  return "기타";
}

/** 홈 화면에 추가한 앱(PWA)으로 열렸는지 — 같은 아이폰이라도 Safari 와 다른 기기로 센다(§10-5) */
export function isStandaloneApp(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || window.matchMedia?.("(display-mode: standalone)").matches === true;
}

export interface DeviceInfo {
  kind: DeviceKind;
  browser: string;
  os: string;
  /** 처음 등록할 때 쓰는 이름 — "Chrome · Windows" / "홈 화면 앱 · iOS" */
  defaultName: string;
}

export const DEVICE_NAME_MAX = 40;

export function describeDevice(env: DeviceEnv, standalone = false): DeviceInfo {
  const kind = detectDeviceKind(env);
  const browser = standalone ? "홈 화면 앱" : parseBrowser(env.userAgent || "");
  const os = parseOs(env);
  return { kind, browser, os, defaultName: `${browser} · ${os}`.slice(0, DEVICE_NAME_MAX) };
}

/** 지금 브라우저의 기기 정보 */
export function currentDeviceInfo(): DeviceInfo {
  if (typeof navigator === "undefined") return { kind: "pc", browser: "브라우저", os: "기타", defaultName: "브라우저 · 기타" };
  return describeDevice({ userAgent: navigator.userAgent, maxTouchPoints: navigator.maxTouchPoints }, isStandaloneApp());
}

/** 활성 기기만, 최근 접속 순 */
export function activeDevicesOf(list: DeviceDoc[]): DeviceDoc[] {
  return list
    .filter((d) => d.status === "active")
    .sort((a, b) => (b.last_seen_at?.getTime() ?? 0) - (a.last_seen_at?.getTime() ?? 0));
}

/** E-3 기본 선택 = 마지막 접속이 가장 오래된 기기 */
export function oldestDeviceId(list: DeviceDoc[]): string | null {
  let pick: DeviceDoc | null = null;
  for (const d of list) {
    const t = d.last_seen_at?.getTime() ?? 0;
    if (!pick || t < (pick.last_seen_at?.getTime() ?? 0)) pick = d;
  }
  return pick?.id ?? null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** 60일 미사용 판정(§7-4). 값이 없으면 만료 아님(기능 적용 직후 기존 사용자 보호) */
export function isInactiveExpired(lastMs: number | null | undefined, nowMs: number): boolean {
  if (!lastMs) return false;
  return nowMs - lastMs > INACTIVE_EXPIRE_DAYS * DAY_MS;
}

/** 이름 입력 정리 — 앞뒤 공백 제거·40자 */
export function normalizeDeviceName(raw: string): string {
  return raw.replace(/\s+/g, " ").trim().slice(0, DEVICE_NAME_MAX);
}

// ── 로그아웃 안내(로그인 화면) ──────────────────────────────────
/** 앱이 스스로 로그아웃시킨 이유 — 로그인 화면 안내 문구 선택용 */
export type LogoutNoticeKind =
  | "idle"
  | "expired"
  | "revoked_self"
  | "revoked_admin"
  | "released"
  | "password_changed"
  | "device_required";

export const LOGOUT_NOTICE_KINDS: readonly LogoutNoticeKind[] = [
  "idle",
  "expired",
  "revoked_self",
  "revoked_admin",
  "released",
  "password_changed",
  "device_required",
] as const;

export function isLogoutNoticeKind(v: unknown): v is LogoutNoticeKind {
  return typeof v === "string" && (LOGOUT_NOTICE_KINDS as readonly string[]).includes(v);
}

export const LOGOUT_NOTICE_TEXT: Record<LogoutNoticeKind, string> = {
  idle: "오래 사용하지 않아 로그아웃됐습니다. 다시 로그인하세요.",
  expired: "60일 동안 사용하지 않아 로그아웃됐습니다. 다시 로그인하세요.",
  revoked_self: "다른 기기에서 이 기기를 로그아웃했습니다.",
  revoked_admin: "관리자가 이 기기를 로그아웃했습니다.",
  released: "다른 기기에서 이 기기를 로그아웃(등록 해제)했습니다. 다시 쓰려면 로그인하세요.",
  password_changed: "비밀번호가 바뀌어 로그아웃됐습니다. 새 비밀번호로 로그인하세요.",
  device_required: "기기 등록이 필요합니다. 다시 로그인하세요.",
};

/**
 * 내 기기 문서를 보고 이 기기가 로그아웃해야 하는지(§7-5). null = 계속 사용.
 *  - status:'revoked'(기기 해제·강제 로그아웃·다른 기기 모두 로그아웃·비번 변경) → 로그아웃
 *  - status:'active' + revoke_requested_at(정지·비번 초기화·관리자 모든 기기 로그아웃 — 함수가 플래그만)
 *    → 그 값이 이 기기의 마지막 접속(last_seen_at, 로그인·앱 열기 때 서버 시각) **이후**일 때만 로그아웃.
 *    옛 플래그가 남아 있어도 다시 로그인한 직후 튕기지 않게(A 계약 2026-10-23). 두 값 모두 서버 시각이라 기기 시계와 무관.
 *    로그인 등록 단계도 플래그를 null 로 지운다(이중 안전).
 */
export function revokeNoticeOf(d: DeviceDoc, myUid: string): LogoutNoticeKind | null {
  const bySelf = d.revoked_by === myUid;
  if (d.status === "revoked") return bySelf ? "released" : "revoked_admin";
  if (d.revoke_requested_at) {
    const seen = d.last_seen_at?.getTime() ?? 0;
    if (d.revoke_requested_at.getTime() <= seen) return null; // 이 세션 전에 찍힌 옛 플래그
    // 기기 등록은 유지(status active) + 본인이 찍은 플래그 = 다른 기기에서 비번 변경 또는 "다른 기기 모두 로그아웃"(§6-2).
    // 사유(revoke_reason)가 있으면 그것으로 구분, 옛 문서(사유 없음)는 일반 문구.
    if (bySelf) return d.revoke_reason === "password_changed" ? "password_changed" : "revoked_self";
    return "revoked_admin";
  }
  return null;
}

// ── 접속 기록 배지 색 ─────────────────────────────────────────
export type AccessLogTone = "blue" | "gray" | "amber" | "red" | "green";

export function accessLogTone(t: AccessLogType): AccessLogTone {
  switch (t) {
    case "login":
    case "device_register":
    case "account_created":
    case "account_reactivated":
    case "membership_added":
      return "blue";
    case "auto_login":
    case "logout":
    case "device_rename":
      return "gray";
    case "forced_logout":
    case "all_devices_logout":
    case "account_suspended":
    case "account_deleted":
      return "red";
    case "password_changed":
    case "password_reset":
      return "green";
    default:
      return "amber";
  }
}

/** 표시용 시각 "2026/10/23 08:40"(dateOnly 면 "2026/10/23"). 없으면 "—" */
export function formatDateTime(d: Date | null | undefined, dateOnly = false): string {
  if (!d) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  const date = `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`;
  return dateOnly ? date : `${date} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
